/* eslint-disable linebreak-style */
import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'components/atoms';
import { Formik, Field } from 'formik';
import { FieldCustom, FieldMarkdown, FieldImageGalleryUpload } from 'components/molecules';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use';
import { linkRegex } from 'services/constants';

const SubWebDepartmentSchema = Yup.object().shape({
  title: Yup.string()
    .min(2, 'Mínimo de 2 caracteres')
    .max(120, 'Máximo de 120 caracteres')
    .required('Campo Obrigatório'),
  description: Yup.string().required('Campo obrigatório'),
  image: Yup.object({
    url: Yup.string()
      .matches(linkRegex, 'O URL inserido não é válido. Certifique-se que começa com http:// ou https:// para sites fora do portal ou com /{lingua}/... para ligações internas.')
      .required('Campo obrigatório'),
    alt: Yup.string(),
    title: Yup.string()
  }),
});

const SubWebDepartmentEditor = ({ initialProps, onChange }) => {

  const [editorProps, setEditorProps] = useState(initialProps);
  const initialValues = useMemo(() => {
    const {
      data: {
        title, description, link, imageUrl, alt, buttonTitle
      }
    } = initialProps;

    return {
      title,
      description,
      link,
      image: {
        url: imageUrl,
        isLocal: imageUrl.indexOf('http') === -1,
        alt: alt !== null ? alt : 'Imagem de destaque'
      },
      buttonTitle
    };
  }, [initialProps]);

  const handleFormChange = (newValues) => {
    // eslint-disable-next-line object-curly-newline
    const { image, link, buttonTitle, ...values } = newValues;
    const newProps = {
      ...initialProps,
      data: {
        ...initialProps.data,
        ...values,
        imageUrl: (image && image.url) ? image.url : null,
        alt: (image && image.alt) ? image.alt : null,
        link: link === '' ? null : link,
        buttonTitle: buttonTitle === '' ? null : buttonTitle
      }
    };

    const isValid = SubWebDepartmentSchema.isValidSync(newValues);
    if (isValid) setEditorProps(newProps);
  };

  useDebounce(
    () => {
      onChange(editorProps);
    },
    200,
    [editorProps]
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={SubWebDepartmentSchema}
      validate={handleFormChange}
      enableReinitialize
    >
      <Form>
        <Field
          title="Título"
          name="title"
          info="Texto identificativo da unidade"
          component={FieldCustom}
          maxLetters={120}
        />
        <Field
          title="Descrição"
          name="description"
          info="Breve descrição sobre a unidade"
          component={FieldMarkdown}
          maxLetters={600}
        />
        <Field
          title="Ligação"
          name="link"
          info="URL para uma página com informação mais completa sobre a unidade"
          component={FieldCustom}
        />
        <Field
          title="Título do Botão (opcional)"
          name="buttonTitle"
          info="Definir nome do botão"
          component={FieldCustom}
        />
        <Field
          title="Imagem"
          name="image"
          info="Imagem ilustrativa da unidade"
          component={FieldImageGalleryUpload}
        />
      </Form>
    </Formik>
  );
};

SubWebDepartmentEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({
    data: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      link: PropTypes.string,
      imageUrl: PropTypes.string,
      buttonTitle: PropTypes.string
    })
  }),
};

SubWebDepartmentEditor.defaultProps = {
  initialProps: {},
};


export default SubWebDepartmentEditor;
